import * as THREE from 'three';
import type { Controller } from './Controller';
import type { Input } from '../core/Input';
import type { TerrainData } from '../world/TerrainData';
import type { OsmData } from '../world/Props';

/**
 * Autopilot along the real Navajo Loop (OSM geometry): Sunset Point -> Wall Street switchbacks ->
 * slot canyon -> loop junction -> Two Bridges -> Thor's Hammer switchbacks -> back to the rim.
 * The visitor can look around freely; any movement key hands control back.
 */
export class GuidedHike {
  active = false;
  speed = 1.35;
  private pts: THREE.Vector2[] = [];
  private cum: number[] = [];
  private s = 0;
  private lookYaw = 0;
  private lookPitch = 0;
  private idle = 0;
  private bob = 0;
  readonly total: number;
  onFootstep: ((surface: 'trail', intensity: number) => void) | null = null;

  constructor(osm: OsmData, private readonly terrain: TerrainData, private readonly ctl: Controller) {
    const way = (pred: (t: OsmData['trails'][number]) => boolean, from: [number, number]) => {
      const t = osm.trails.find(pred);
      if (!t) return [] as [number, number][];
      const a = t.pts[0], b = t.pts[t.pts.length - 1];
      const da = Math.hypot(a[0] - from[0], a[1] - from[1]), db = Math.hypot(b[0] - from[0], b[1] - from[1]);
      return da <= db ? t.pts : [...t.pts].reverse();
    };
    const route: [number, number][] = [[7, 22]];
    const append = (p: [number, number][]) => { for (const q of p) route.push(q); };
    const last = () => route[route.length - 1];
    append(way((t) => t.alt === 'Wall Street Trail' && t.pts.length > 50 && t.pts.length < 150, last()));
    append(way((t) => t.alt === 'Wall Street Trail' && t.pts.length === 2, last()));
    append(way((t) => t.alt === 'Wall Street Trail' && t.pts.length > 150, last()));
    append(way((t) => t.alt === 'Two Bridges Trail', last()));
    route.push([-3, 12], [7, 22]);
    // densify & smooth
    const raw = route.map(([x, z]) => new THREE.Vector2(x, z));
    const dense: THREE.Vector2[] = [];
    for (let i = 0; i < raw.length - 1; i++) {
      const a = raw[i], b = raw[i + 1];
      const n = Math.max(1, Math.ceil(a.distanceTo(b) / 0.5));
      for (let k = 0; k < n; k++) dense.push(a.clone().lerp(b, k / n));
    }
    dense.push(raw[raw.length - 1].clone());
    // light smoothing to round the polyline corners
    for (let it = 0; it < 3; it++) {
      for (let i = 1; i < dense.length - 1; i++) dense[i].lerp(dense[i - 1].clone().add(dense[i + 1]).multiplyScalar(0.5), 0.5);
    }
    this.pts = dense;
    this.cum = [0];
    for (let i = 1; i < dense.length; i++) this.cum.push(this.cum[i - 1] + dense[i].distanceTo(dense[i - 1]));
    this.total = this.cum[this.cum.length - 1];
  }

  private at(s: number, out = new THREE.Vector2()) {
    s = THREE.MathUtils.clamp(s, 0, this.total);
    let lo = 0, hi = this.cum.length - 1;
    while (hi - lo > 1) { const m = (lo + hi) >> 1; if (this.cum[m] <= s) lo = m; else hi = m; }
    const t = (s - this.cum[lo]) / Math.max(this.cum[hi] - this.cum[lo], 1e-6);
    return out.copy(this.pts[lo]).lerp(this.pts[hi], t);
  }

  start() {
    // resume from the closest point of the loop
    const p = new THREE.Vector2(this.ctl.position.x, this.ctl.position.z);
    let best = 0, bd = Infinity;
    for (let i = 0; i < this.pts.length; i++) { const d = this.pts[i].distanceToSquared(p); if (d < bd) { bd = d; best = i; } }
    this.s = bd < 60 * 60 ? this.cum[best] : 0;
    this.active = true;
    this.ctl.autopilot = true;
    this.lookYaw = this.lookPitch = 0;
    this.ctl.setMode('walk');
  }

  stop() {
    if (!this.active) return;
    this.active = false;
    this.ctl.autopilot = false;
    this.ctl.velocity.set(0, 0, 0);
    this.ctl.bobOffset.set(0, 0, 0);
  }

  get progress() {
    return this.s / this.total;
  }

  update(dt: number, input: Input) {
    if (!this.active) return;
    const moveKeys = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (moveKeys.some((k) => input.keys.has(k)) || Math.abs(input.stick.x) + Math.abs(input.stick.y) > 0.2) { this.stop(); return; }
    const fast = input.keys.has('ShiftLeft') || input.keys.has('ShiftRight');
    const v = this.speed * (fast ? 4 : 1);
    this.s += v * dt;
    if (this.s >= this.total) { this.s = this.total; this.stop(); return; }
    const p = this.at(this.s);
    const ahead = this.at(this.s + 6);
    const g = this.terrain.heightAt(p.x, p.y);
    this.ctl.position.set(p.x, g + this.ctl.eyeHeight, p.y);
    // heading follows the trail; mouse look adds an offset that relaxes when idle
    const pathYaw = Math.atan2(ahead.x - p.x, -(ahead.y - p.y));
    const ahead2 = this.at(this.s + 10);
    const gA = this.terrain.heightAt(ahead2.x, ahead2.y);
    const pathPitch = THREE.MathUtils.clamp(Math.atan2(gA - g, 10) * 0.6, -0.5, 0.4) - 0.04;
    const lookMoved = Math.abs(input.lookDX) + Math.abs(input.lookDY) > 0;
    if (lookMoved) this.idle = 0; else this.idle += dt;
    // input.look was already applied to ctl.yaw/pitch by Controller.update; convert into an offset
    const desiredYaw = pathYaw + this.lookYaw;
    let dy = this.ctl.yaw - desiredYaw;
    dy = Math.atan2(Math.sin(dy), Math.cos(dy));
    if (lookMoved) this.lookYaw += dy; // user turned the head
    this.lookPitch = this.ctl.pitch - pathPitch;
    if (this.idle > 2.5) {
      const k = 1 - Math.exp(-dt * 0.8);
      this.lookYaw *= 1 - k;
      this.lookPitch *= 1 - k;
    }
    const smooth = 1 - Math.exp(-dt * 2.5);
    let target = pathYaw + this.lookYaw;
    let d = target - this.ctl.yaw;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    target = this.ctl.yaw + d;
    this.ctl.yaw = lookMoved ? this.ctl.yaw : this.ctl.yaw + (target - this.ctl.yaw) * smooth;
    this.ctl.pitch = lookMoved ? this.ctl.pitch : this.ctl.pitch + (pathPitch + this.lookPitch - this.ctl.pitch) * smooth;
    // walking bob + footsteps
    const prev = this.bob;
    this.bob += dt * (1.6 + v * 0.55) * Math.PI;
    if (Math.floor(prev / Math.PI) !== Math.floor(this.bob / Math.PI)) this.onFootstep?.('trail', Math.min(1, 0.45 + v / 5));
    this.ctl.bobOffset.set(Math.cos(this.bob) * 0.022, Math.abs(Math.sin(this.bob)) * 0.04, 0);
  }
}
